import React from 'react'
import { BasketButton, MaxWidthConstraint, Spacer, WorkArea } from '@revelrygroup/components'
import classnames from 'classnames'
import Image from 'next/image'
import router from 'next/router'

import CartModel from '../../models/CartModel'
import styles from './styles.module.scss'
import MemorialTreeForm, { FormValues } from '../../../forms/MemorialTreeForm'
import { MemorialTree, ObituarySnippet } from '../../../types'
import useCartContext from '../../../hooks/useCartContext'
import { routes } from '../../../routes'
import Basket from '../../Basket'
import EventTrackingModel from '../../models/EventTrackingModel'
import debounce from '../../../utils/debounce'

interface Props {
  memorialTrees: MemorialTree[]
  obituary: ObituarySnippet
}

const MemorialTreesPage: React.FC<Props> = ({ memorialTrees, obituary }) => {
  const [basketVisible, setBasketVisible] = React.useState(false)
  const [cartCtaLoading, setCartCtaLoading] = React.useState(false)
  const ctx = useCartContext()
  const onOpenBasket = (): void => {
    setBasketVisible(true)
    document.body.classList.add('no-scroll')
  }
  const onCloseBasket = (): void => {
    setBasketVisible(false)
    document.body.classList.remove('no-scroll')
  }
  const goToNextPage = (cartID: string) => {
    if (CartModel.hasFlowerProducts(ctx.cart)) {
      router.push(routes.shipping(obituary, cartID))
    } else {
      router.push(routes.billing(obituary, cartID))
    }
  }
  const onAddToCart = debounce(
    async (product: MemorialTree): Promise<void> => {
      await ctx.addTreeProductToCart(product)
      onOpenBasket()
    },
  )
  const onBuyNow = debounce(
    async (product: MemorialTree): Promise<void> => {
      const updatedCart = await ctx.addTreeProductToCart(product)

      EventTrackingModel.trackTreeAdded(product)
      EventTrackingModel.trackCheckoutInitiated(updatedCart)
      goToNextPage(updatedCart.external_id)
    },
  )
  const onSubmit = debounce(async (values: FormValues) => {
    setCartCtaLoading(true)
    const treeProduct = memorialTrees.find((tree) => tree.product_id === values.product_id)
    const productExistsInCart = ctx.cart.memorial_tree_shopping_cart_items.some(
      (item) => item.product_id === treeProduct?.product_id,
    )
    let userConfirmedSubmission = true

    try {
      if (productExistsInCart) {
        userConfirmedSubmission = confirm(
          'This item already exists in your shopping cart. Would you like to add another?',
        )
      }
      if (treeProduct && userConfirmedSubmission) {
        return values.buy_now ? await onBuyNow(treeProduct) : await onAddToCart(treeProduct)
      }
    } finally {
      setCartCtaLoading(false)
    }
  })

  return (
    <>
      <WorkArea avoidHeader>
        <MaxWidthConstraint maxWidth={960}>
          <Spacer verticalBottom="sm">
            <div className={styles.headerRow}>
              {ctx.totalProductQuantity > 0 && (
                <BasketButton productCount={ctx.totalProductQuantity} onClick={onOpenBasket} />
              )}
            </div>
          </Spacer>

          <div className={styles.content}>
            <div className={styles.left}>
              <Image alt="Memorial tree" src="/memorial-tree.jpg" height={400} width={400} />
            </div>

            <div className={styles.right}>
              <Spacer verticalBottom="xs" verticalTop="sm" verticalMdTop="none">
                <h1 className="h2">Plant Memorial Trees</h1>
              </Spacer>

              <p className="body">
                Trees clean our air and water, create habitats for biodiversity, contribute to our
                health and wellbeing, and create jobs for social impact.
              </p>

              <p className="body">
                Honor a lost loved one with meaningful gift that will last for years to come. Your
                memorial tribute will go towards planting a tree in Appalachia.
              </p>

              <Spacer verticalBottom="sm" verticalTop="xs">
                <div className={styles.matchWrapper}>
                  <Image alt="Tree match" src="/tree-match.svg" height={28} width={110} />

                  <Spacer verticalTop="xs">
                    <h3 className="body bold">1 for 1 match</h3>
                  </Spacer>

                  <p className={classnames('body', 'no-margin')}>
                    For each tree purchased, Kalas Funeral Home will match your order with an
                    additional memorial tree.
                  </p>
                </div>
              </Spacer>

              <MemorialTreeForm
                initialValues={{ product_id: memorialTrees[0]?.product_id || '' }}
                memorialTrees={memorialTrees}
                onSubmit={onSubmit}
                submitDisabled={cartCtaLoading}
              />

              <Spacer verticalTop="sm">
                <p className="body">
                  We partner with various 501(c)(3) nonprofits that each have a mission to make it
                  simple for anyone to help the environment by planting trees. Their projects span
                  the globe and are done in partnership with local communities and knowledgeable
                  experts to create an impact for nature, people, and wildlife. Reforestation helps
                  to rebuild forests after fires and floods, provide jobs for social impact, and
                  restore biodiversity. Many projects have overlapping objectives, creating a
                  combination of benefits that contribute to the UN&apos;s Sustainable Development
                  Goals.
                </p>
              </Spacer>
            </div>
          </div>
        </MaxWidthConstraint>
      </WorkArea>

      {basketVisible && (
        <Basket
          onCheckout={() => {
            EventTrackingModel.trackCheckoutInitiated(ctx.cart)

            goToNextPage(ctx.cartID)
          }}
          onClose={onCloseBasket}
        />
      )}
    </>
  )
}

export default MemorialTreesPage
